import { type FC, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { _isNotEmpty, _notNil } from '@/littledash';
import { errorToast, successToast } from '@/helpers';

import type { SpeciesV1 } from '@/model/Species.model';

import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { DateUtils } from '@/utils/Date.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import DraggableList, { asListItem } from '@/components/UI/List/DraggableList';

import AnimalFormFields from './AnimalFormFields';

interface CageSubject {
  id: number;
}

interface Cage {
  id: number;
  api_id: string;
  name: string;
  subjects?: {
    data?: Array<CageSubject>;
  };
}

interface AddAnimalsProps {
  cage: Cage;
  handleCallback?: (response: unknown, cage: Cage) => void;
  closeModal: () => void;
}

type AnimalSex = 'm' | 'f' | 'u';

interface AnimalFormValues {
  cage_id: string;
  name: string;
  sex: AnimalSex;
  dob: string | null;
  strain_id: string | null;
}

interface AddAnimalsFormData {
  animals: Array<AnimalFormValues>;
}

const pluralize = (count: number, word: string): string => `${count} ${word}${count !== 1 ? 's' : ''}`;

const getPopulationSubText = (currentPopulation: number): string | undefined => {
  if (currentPopulation >= MAX_CAGE_POPULATION) {
    return 'This cage has reached its animal population limit';
  }
  if (currentPopulation >= 0) {
    const spacesLeft = MAX_CAGE_POPULATION - currentPopulation;
    return `There are currently ${pluralize(currentPopulation, 'animal')} in this cage. ${spacesLeft} more spaces are available.`;
  }
  return undefined;
};

const AddAnimals: FC<AddAnimalsProps> = ({ cage, handleCallback, closeModal }) => {
  const currentCagePopulation = Number(cage?.subjects?.data?.length ?? 0);

  // Species data
  const [selectableSpecies, setSpecies] = useState<Array<SpeciesV1>>([]);
  const { response: speciesResponse, loading: speciesLoading } = useApiHook({
    endpoint: 'GET /api/v1/species',
    invokeOnInit: true,
    query: { include: ['strains'] },
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  useEffect(() => {
    if (_notNil(speciesResponse) && speciesResponse.type === 'success') {
      setSpecies(speciesResponse?.body?.data as Array<SpeciesV1>);
    }
  }, [speciesResponse]);

  // Form setup
  const newAnimal: AnimalFormValues = {
    cage_id: cage.api_id,
    name: `Animal ${currentCagePopulation + 1}`,
    sex: 'm',
    dob: DateUtils.formatISODateTimeToDate(DateUtils.dateTimeNow()),
    strain_id: null,
  };

  const formMethods = useForm<AddAnimalsFormData>({
    defaultValues: { animals: [{ ...newAnimal }] },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;

  const { fields: animals, append: appendAnimal, remove: removeAnimal } = useFieldArray({ control, name: 'animals' });

  // API mutation
  const {
    invoke: createAnimals,
    loading: creatingAnimals,
    response: createResponse,
  } = useApiHook({
    endpoint: 'POST /api/v1/animals',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'animal-create' } },
  });

  // Handlers
  const onAppendAnimal = () => {
    appendAnimal({
      ...newAnimal,
      name: `Animal ${currentCagePopulation + animals.length + 1}`,
    });
  };

  const onSubmit = async (data: AddAnimalsFormData) => {
    const transformedData = {
      animals: data.animals.map((animal) => ({
        ...animal,
        dob: animal.dob === '' ? null : animal.dob,
      })),
    };

    const result = await createAnimals({ body: transformedData });
    if (result.type === 'success') {
      successToast(`Created ${pluralize(animals.length, 'animal')} in ${cage.name}`);
      if (_notNil(handleCallback)) {
        handleCallback({ data: result.body }, cage);
      }
    } else {
      errorToast('Could not create animals');
    }
  };

  // Loading state
  if (speciesLoading) {
    return <Loading />;
  }

  return (
    <ModalContainer size="narrow" className="bg-white">
      <ModalHeader
        title={`Add animals to ${cage.name}`}
        subText={getPopulationSubText(currentCagePopulation)}
        closeModal={closeModal}
        closeDisabled={creatingAnimals}
        className="pa3 bb b--moon-gray w-100"
      />

      {createResponse?.type === 'error' && <ApiErrorBanner error={createResponse.error} />}

      <FormProvider {...formMethods}>
        <DraggableList
          canAmend
          canAdd={animals.length + currentCagePopulation < MAX_CAGE_POPULATION}
          canRemove={animals.length > 1}
          data={animals}
          onAddClick={onAppendAnimal}
          onRemoveClick={removeAnimal}
          addButtonText="Add new animal"
          renderItem={asListItem(AnimalFormFields, {
            selectableSpecies,
          })}
          containerProps={{ className: 'pa3' }}
        />
      </FormProvider>

      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        cancelBtnText="Cancel"
        submitButtonProps={{
          disabled: creatingAnimals || _isNotEmpty(Object.keys(errors)),
          loading: creatingAnimals,
        }}
        cancelButtonProps={{ disabled: creatingAnimals }}
      />
    </ModalContainer>
  );
};

export default AddAnimals;
